import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { EmployeeService } from '../../services/employee.service';
import { AllocationService } from '../../services/allocation.service';
import { CategoryService } from '../../services/category.service';
import { Employee } from '../../models/employee.model';
import { Allocation, Month } from '../../models/allocation.model';
import { Category } from '../../models/category.model';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/** Project with duration derived from monthly allocations (first–last month). */
export interface ProjectWithDuration {
  projectName: string;
  /** e.g. "Jan 2024 - Mar 2025" */
  durationLabel: string;
  /** Number of months with allocation. */
  monthsCount: number;
}

interface EmployeeWithProjects {
  employeeId: string;
  employeeName: string;
  projects: string[];
  /** Projects with duration based on allocation months. */
  projectEntries: ProjectWithDuration[];
  expanded: boolean;
  /** False for leavers — the row is badged so history is not mistaken for current staffing. */
  isActive: boolean;
}

interface CategoryWithEmployees {
  categoryId: string;
  categoryName: string;
  employees: EmployeeWithProjects[];
  expanded: boolean;
}

@Component({
  selector: 'app-employee-projects-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    
  ],
  templateUrl: './employee-projects-history.component.html',
  styleUrl: './employee-projects-history.component.scss'
})
export class EmployeeProjectsHistoryComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  categoryData: CategoryWithEmployees[] = [];
  filteredCategoryData: CategoryWithEmployees[] = [];
  loading: boolean = true;
  searchText: string = '';
  
  employees: Employee[] = [];
  categories: Category[] = [];

  constructor(
    private employeeService: EmployeeService,
    private allocationService: AllocationService,
    private categoryService: CategoryService,
    private messageService: MessageService,
    private pageHeaderService: PageHeaderSearchActionService
  ) { }

  get totalCategoriesCount(): number {
    return this.categoryData.length;
  }

  get totalEmployeesCount(): number {
    return this.categoryData.reduce((sum, c) => sum + c.employees.length, 0);
  }

  get totalProjectsCount(): number {
    return this.categoryData.reduce((sum, c) =>
      sum + c.employees.reduce((eSum, e) => eSum + e.projectEntries.length, 0), 0);
  }

  get showingCategoriesCount(): number {
    return this.filteredCategoryData.length;
  }

  ngOnInit() {
    this.pageHeaderService.setPlaceholder('Search employees or projects...');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(text => {
      this.searchText = text;
      this.onSearch();
    });
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.exportToExcel();
    });
    this.loadData();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadData() {
    this.loading = true;
    
    // Get current date to determine which months to fetch
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 1-12
    
    // Fetch data for last 24 months
    const allocationRequests: { [key: string]: any } = {};
    for (let i = 0; i < 24; i++) {
      let year = currentYear;
      let month = currentMonth - i;
      
      while (month <= 0) {
        month += 12;
        year -= 1;
      }
      
      const key = `${year}_${month}`;
      allocationRequests[key] = this.allocationService.getAllAllocations(year, month as Month).pipe(
        catchError(() => of([] as Allocation[]))
      );
    }

    forkJoin({
      employees: this.employeeService.getAllEmployees().pipe(
        catchError(error => {
          console.error('Error loading employees:', error);
          return of([] as Employee[]);
        })
      ),
      categories: this.categoryService.getAllCategories().pipe(
        catchError(error => {
          console.error('Error loading categories:', error);
          return of([] as Category[]);
        })
      ),
      ...allocationRequests
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        // Deliberately NOT filtered by isActive. This is a HISTORY report: a leaver's past
        // project work is exactly what it exists to show, and dropping inactive employees made
        // people vanish from it the moment they were deactivated — which happens during
        // offboarding, while managers still need the record.
        this.employees = (data.employees || []);
        this.categories = (data.categories || []).filter((c: Category) => c.isActive);
        
        // Aggregate all allocations
        const allAllocations: Allocation[] = [];
        Object.keys(allocationRequests).forEach(key => {
          const allocations = data[key] as Allocation[];
          if (allocations) {
            allAllocations.push(...allocations);
          }
        });
        
        // Build category-based structure
        this.buildCategoryStructure(allAllocations);
        this.applyFilters();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load employee projects history'
        });
        this.loading = false;
      }
    });
  }

  private static MONTH_NAMES: string[] = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** Build duration label and count from list of (year, month) from monthly allocations. */
  private computeProjectDuration(months: { year: number; month: number }[]): { label: string; monthsCount: number } {
    if (months.length === 0) {
      return { label: '–', monthsCount: 0 };
    }
    const sorted = [...months].sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const startStr = `${EmployeeProjectsHistoryComponent.MONTH_NAMES[first.month]} ${first.year}`;
    const endStr = last === first
      ? ''
      : ` - ${EmployeeProjectsHistoryComponent.MONTH_NAMES[last.month]} ${last.year}`;
    const label = endStr ? `${startStr}${endStr}` : startStr;
    return { label, monthsCount: sorted.length };
  }

  private buildCategoryStructure(allocations: Allocation[]) {
    // Group employees by category
    const categoryMap = new Map<string, CategoryWithEmployees>();
    
    // Initialize categories
    this.categories.forEach(cat => {
      categoryMap.set(cat.id, {
        categoryId: cat.id,
        categoryName: cat.name,
        employees: [],
        expanded: false
      });
    });
    
    // Group employees by category
    const employeeMap = new Map<string, EmployeeWithProjects>();
    
    this.employees.forEach(emp => {
      const categoryId = emp.categoryId || 'uncategorized';
      const category = categoryMap.get(categoryId);
      
      if (!category) {
        // Create uncategorized category if it doesn't exist
        categoryMap.set('uncategorized', {
          categoryId: 'uncategorized',
          categoryName: 'Uncategorized',
          employees: [],
          expanded: false
        });
      }
      
      const employeeKey = emp.id;
      if (!employeeMap.has(employeeKey)) {
        employeeMap.set(employeeKey, {
          employeeId: emp.id,
          employeeName: emp.name,
          projects: [],
          projectEntries: [],
          expanded: false,
          isActive: emp.isActive !== false
        });
      }
    });

    // Collect (year, month) per employee per project from allocations
    const employeeProjectMonths = new Map<string, Map<string, { year: number; month: number }[]>>();
    allocations.forEach(alloc => {
      if (!alloc.employeeId || !alloc.projectName) return;
      const key = alloc.employeeId;
      if (!employeeProjectMonths.has(key)) {
        employeeProjectMonths.set(key, new Map());
      }
      const projectMap = employeeProjectMonths.get(key)!;
      const y = alloc.year;
      const m = typeof alloc.month === 'number' ? alloc.month : (alloc.month as number);
      if (!projectMap.has(alloc.projectName)) {
        projectMap.set(alloc.projectName, []);
      }
      const list = projectMap.get(alloc.projectName)!;
      if (!list.some(p => p.year === y && p.month === m)) {
        list.push({ year: y, month: m });
      }
    });

    // Aggregate projects and build duration per employee per project
    allocations.forEach(alloc => {
      const employee = employeeMap.get(alloc.employeeId);
      if (employee && alloc.projectName) {
        if (!employee.projects.includes(alloc.projectName)) {
          employee.projects.push(alloc.projectName);
        }
      }
    });

    // Build projectEntries with duration for each employee
    employeeMap.forEach((employee) => {
      const projectMonths = employeeProjectMonths.get(employee.employeeId);
      employee.projectEntries = employee.projects.map((projectName) => {
        const months = projectMonths?.get(projectName) ?? [];
        const duration = this.computeProjectDuration(months);
        return {
          projectName,
          durationLabel: duration.label,
          monthsCount: duration.monthsCount
        };
      });
      employee.projects.sort();
      employee.projectEntries.sort((a, b) => a.projectName.localeCompare(b.projectName));
    });
    
    // Add employees to their respective categories
    this.employees.forEach(emp => {
      const categoryId = emp.categoryId || 'uncategorized';
      const category = categoryMap.get(categoryId);
      const employee = employeeMap.get(emp.id);
      
      if (category && employee) {
        category.employees.push(employee);
      }
    });
    
    // Sort employees within each category
    categoryMap.forEach(category => {
      category.employees.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
    });
    
    // Convert to array and sort by category name
    this.categoryData = Array.from(categoryMap.values())
      .filter(cat => cat.employees.length > 0) // Only show categories with employees
      .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  }

  applyFilters() {
    let filtered = [...this.categoryData];
    
    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.map(category => {
        const filteredEmployees = category.employees.filter(emp =>
          emp.employeeName?.toLowerCase().includes(searchLower) ||
          emp.projects.some(p => p.toLowerCase().includes(searchLower))
        );
        
        if (filteredEmployees.length > 0) {
          return {
            ...category,
            employees: filteredEmployees.map(emp => {
              const filteredProjects = emp.projects.filter(p =>
                p.toLowerCase().includes(searchLower) ||
                emp.employeeName?.toLowerCase().includes(searchLower)
              );
              const filteredEntries = emp.projectEntries.filter(e =>
                filteredProjects.includes(e.projectName)
              );
              return {
                ...emp,
                projects: filteredProjects,
                projectEntries: filteredEntries
              };
            })
          };
        }
        return null;
      }).filter(cat => cat !== null) as CategoryWithEmployees[];
    }
    
    this.filteredCategoryData = filtered;
  }

  onSearch() {
    this.applyFilters();
  }

  clearSearch() {
    this.searchText = '';
    this.applyFilters();
  }

  toggleCategory(category: CategoryWithEmployees) {
    category.expanded = !category.expanded;
  }

  toggleEmployee(employee: EmployeeWithProjects) {
    employee.expanded = !employee.expanded;
  }

  expandAll() {
    this.filteredCategoryData.forEach(category => {
      category.expanded = true;
      category.employees.forEach(employee => {
        employee.expanded = true;
      });
    });
  }

  collapseAll() {
    this.filteredCategoryData.forEach(category => {
      category.expanded = false;
      category.employees.forEach(employee => {
        employee.expanded = false;
      });
    });
  }

  getProjectsDisplay(projects: string[]): string {
    if (projects.length === 0) {
      return 'No projects';
    }
    return projects.join(', ');
  }

  exportToExcel() {
    if (!this.filteredCategoryData || this.filteredCategoryData.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No data to export'
      });
      return;
    }

    try {
      const exportData: any[] = [];
      
      this.filteredCategoryData.forEach(category => {
        category.employees.forEach(employee => {
          const projectsWithDuration = employee.projectEntries
            .map(e => `${e.projectName} (${e.durationLabel})`)
            .join('; ') || 'No projects';
          exportData.push({
            'Category': category.categoryName,
            'Employee Name': employee.employeeName,
            'Projects': employee.projects.join(', ') || 'No projects',
            'Project Duration (from allocation)': projectsWithDuration
          });
        });
      });

      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      
      const columnWidths = [
        { wch: 20 }, // Category
        { wch: 30 }, // Employee Name
        { wch: 50 }, // Projects
        { wch: 55 }  // Project Duration
      ];
      worksheet['!cols'] = columnWidths;

      const workbook: XLSX.WorkBook = {
        Sheets: { 'Employee Projects History': worksheet },
        SheetNames: ['Employee Projects History']
      };

      const excelBuffer: any = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      const data: Blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const fileName = `Employee_Projects_History_${this.formatDate(new Date())}.xlsx`;
      saveAs(data, fileName);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Employee projects history exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export data to Excel'
      });
    }
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }
}
